import React from 'react'

import { Filter } from '@cubejs-client/core'
import { isEmpty } from 'ramda'

import { Flex, IconButton, Table } from '@driftt/dds'
import { faEnvelope } from '@driftt/dds.icons/light/faEnvelope'
import { ChartCard, TypeFormats } from '@driftt/dds.reports'

import { SchedulingEventSummaryMetrics } from 'api/report/types'
import ExportButton from 'components/core/ExportButton'
import LeadCell from 'components/core/LeadCell'
import useQueryConfig from 'hooks/useQueryConfig'
import useSortConfig from 'hooks/useSortConfig'
import { createCSVDownload, csvFilenameFromQuery } from 'utils/csv'

import { toCSV } from './formatMeetingExport'
import useMeetingRequests from './useMeetingRequests.hook'

export interface ChartProps {
  metric: string
  filters?: Filter[]
}

const defaultEmptyValue = 'N/A'

const UnqualifiedMeetingRequestsChart = ({
  metric,
  filters = [],
}: ChartProps) => {
  const { queryConfig } = useQueryConfig(metric)

  const [sortConfig] = useSortConfig(SchedulingEventSummaryMetrics.CREATED_AT)
  const {
    handleTableStateChange,
    partialMetric: paginatedMeetingRequests,
    fullMetric: meetingRequests,
    totalRowCount,
    pagination,
    isLoading,
    isError,
  } = useMeetingRequests(sortConfig, queryConfig, filters)

  const onExportClick = () => {
    createCSVDownload(toCSV(meetingRequests), csvFilenameFromQuery(queryConfig))
  }

  return (
    <ChartCard
      css={{
        paddingX: 0,
        paddingBottom: 0,
      }}
    >
      <Flex
        fullWidth
        grow={1}
        css={{
          paddingX: '$3',
        }}
      >
        <ChartCard.Header
          title="Unqualified meeting requests"
          subtitle="Which site visitors who did not qualify for automatic booking have requested a meeting?"
          action={
            <ExportButton
              isDisabled={isEmpty(meetingRequests)}
              onClick={onExportClick}
              queryConfig={queryConfig}
            />
          }
        />
      </Flex>
      <ChartCard.Content>
        {!isError && (
          <Table
            disableBoxShadow
            isLoading={isLoading}
            rows={paginatedMeetingRequests ?? []}
            columns={[
              {
                value: 'endUser',
                header: 'Name',
                cell: ({ endUser }) => <LeadCell endUser={endUser} />,
              },
              {
                value: 'companyName',
                header: 'Company',
                cell: ({ companyName }) => companyName || defaultEmptyValue,
              },
              {
                header: 'Industry',
                value: 'companyIndustry',
                cell: ({ companyIndustry }) =>
                  companyIndustry || defaultEmptyValue,
              },
              {
                header: 'Company size',
                value: 'companyEmployeesRange',
                cell: ({ companyEmployeesRange }) =>
                  companyEmployeesRange || defaultEmptyValue,
              },
              {
                header: 'Company location',
                value: 'companyCountry',
                cell: ({ companyCountry }) =>
                  companyCountry || defaultEmptyValue,
              },
              {
                header: 'Requested on',
                size: 64,
                value: 'createdAt',
                align: 'right',
                cell: ({ createdAt }) => TypeFormats.DATE(new Date(createdAt)),
              },
              {
                header: '',
                value: 'endUser.email',
                size: 64,
                cell: ({ endUser }) => (
                  <IconButton
                    icon={faEnvelope}
                    title=""
                    onClick={() => {
                      window.location.href = `mailto:${endUser.email}`
                    }}
                  />
                ),
              },
            ]}
            onStateChange={handleTableStateChange}
            pagination={{
              rowTotal: totalRowCount ?? 0,
              defaultPagination: pagination,
            }}
          />
        )}
      </ChartCard.Content>
    </ChartCard>
  )
}

export default UnqualifiedMeetingRequestsChart
